import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, X, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Lighthouse, Visit, VisitPhoto } from '../types';
import { addVisitPhotos, deleteVisitPhoto, MAX_PHOTOS_PER_STAMP } from '../lib/photos';
import LighthouseDetail from './LighthouseDetail';
import '../styles/Dashboard.css';

const TIERS = ['All', 'Standard', 'Extreme', 'Heritage'];

// Member ranks, earned by verified stamps. Edit the names here any time.
const RANKS = [
  { stamps: 10, name: 'Supernumerary' },
  { stamps: 25, name: 'Assistant Keeper' },
  { stamps: 50, name: 'Keeper' },
  { stamps: 100, name: 'Head Keeper' },
  { stamps: 183, name: 'Keeper of All Lights' },
];

function rankInfo(stamps: number) {
  let current: { stamps: number; name: string } | null = null;
  let next: { stamps: number; name: string } | null = null;
  for (const r of RANKS) {
    if (stamps >= r.stamps) current = r;
    else if (!next) next = r;
  }
  return { current, next };
}

interface Props {
  appUserId: string | null;
  readOnly?: boolean;
}

function DashboardPage({ appUserId, readOnly = false }: Props) {
  const [lighthouses, setLighthouses] = useState<Lighthouse[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [tier, setTier] = useState('All');
  const [state, setState] = useState('All');
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState<VisitPhoto[]>([]);
  const [viewing, setViewing] = useState<VisitPhoto | null>(null);
  const [detailLh, setDetailLh] = useState<Lighthouse | null>(null);
  const [sharePhotos, setSharePhotos] = useState(true);
  const [openId, setOpenId] = useState<number | null>(null);
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const pendingVisit = useRef<Visit | null>(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const [subTier, setSubTier] = useState('Standard');
  const [subLighthouseId, setSubLighthouseId] = useState<number | ''>('');
  const [subDate, setSubDate] = useState('');
  const [subPhoto, setSubPhoto] = useState<File | null>(null);
  const [subBusy, setSubBusy] = useState(false);
  const [subMessage, setSubMessage] = useState('');

  useEffect(() => {
    const load = async () => {
      const { data: lh } = await supabase
        .from('lighthouses')
        .select('*')
        .order('name');
      setLighthouses(lh || []);
      if (appUserId) {
        const { data: v } = await supabase
          .from('visits')
          .select('*')
          .eq('user_id', appUserId);
        const { data: u } = await supabase
          .from('users')
          .select('share_photos')
          .eq('id', appUserId)
          .single();
        if (u && typeof u.share_photos === 'boolean') setSharePhotos(u.share_photos);
        setVisits(v || []);
        const ids = (v || []).map((x) => x.id);
        if (ids.length) {
          const { data: p } = await supabase
            .from('visit_photos')
            .select('*')
            .in('visit_id', ids)
            .order('created_at');
          setPhotos(p || []);
        }
      }
      setLoading(false);
    };
    load();
  }, [appUserId]);

  const visitFor = (lighthouseId: number) =>
    visits.find((v) => v.lighthouse_id === lighthouseId);

  const pickPhoto = (visit: Visit) => {
    pendingVisit.current = visit;
    fileInput.current?.click();
  };

  const photosFor = (visitId: number) =>
    photos.filter((p) => p.visit_id === visitId);

  const onPhotoChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const visit = pendingVisit.current;
    e.target.value = '';
    if (!files.length || !visit || !appUserId) return;
    const room = MAX_PHOTOS_PER_STAMP - photosFor(visit.id).length;
    if (room <= 0) return;
    setUploadingId(visit.lighthouse_id);
    const added = await addVisitPhotos(
      appUserId,
      visit.lighthouse_id,
      visit.id,
      files.slice(0, room)
    );
    if (added.length) setPhotos((prev) => [...prev, ...added]);
    setUploadingId(null);
  };

  const removePhoto = async (photo: VisitPhoto) => {
    if (!window.confirm('Remove this photo?')) return;
    const ok = await deleteVisitPhoto(photo);
    if (ok) setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
  };

  const toggleSharePhotos = async () => {
    const next = !sharePhotos;
    setSharePhotos(next);
    const { error } = await supabase.rpc('set_share_photos', { share: next });
    if (error) setSharePhotos(!next);
  };

  const submitPastVisit = async () => {
    if (!appUserId || !subLighthouseId) {
      setSubMessage('Pick a lighthouse.');
      return;
    }
    if (!subDate) {
      setSubMessage('Enter the date you visited.');
      return;
    }
    if (!subPhoto) {
      setSubMessage('Add a photo of you at the lighthouse. It is how we verify past visits.');
      return;
    }
    const existing = visits.find((v) => v.lighthouse_id === subLighthouseId);
    if (existing) {
      setSubMessage(existing.verified ? 'You already have a stamp for that lighthouse.' : 'That one is already submitted and waiting for review.');
      return;
    }
    setSubBusy(true);
    setSubMessage('');
    const { data: created, error } = await supabase
      .from('visits')
      .insert({
        user_id: appUserId,
        lighthouse_id: subLighthouseId,
        visited_at: new Date(subDate + 'T12:00:00').toISOString(),
        verified: false,
      })
      .select('*')
      .single();
    if (error) {
      setSubMessage(error.message);
    } else if (created) {
      if (subPhoto) {
        const added = await addVisitPhotos(appUserId, subLighthouseId as number, created.id, [subPhoto]);
        if (added.length) setPhotos((prev) => [...prev, ...added]);
      }
      setVisits((prev) => [...prev, created]);
      const name = lighthouses.find((l) => l.id === subLighthouseId)?.name || 'Visit';
      setSubMessage(name + ' submitted for review. It will show as Pending until we verify it.');
      setSubLighthouseId('');
      setSubDate('');
      setSubPhoto(null);
    }
    setSubBusy(false);
  };

  const visitedIds = useMemo(
    () => new Set(visits.filter((v) => v.verified).map((v) => v.lighthouse_id)),
    [visits]
  );
  const pendingIds = useMemo(
    () =>
      new Set(visits.filter((v) => !v.verified).map((v) => v.lighthouse_id)),
    [visits]
  );

  const states = useMemo(
    () => ['All', ...Array.from(new Set(lighthouses.map((l) => l.state))).sort()],
    [lighthouses]
  );

  const filtered = lighthouses.filter(
    (l) =>
      (tier === 'All' || l.passport_type === tier) &&
      (state === 'All' || l.state === state)
  );

  const tierCount = (t: string) => {
    const inTier = lighthouses.filter((l) => l.passport_type === t);
    const done = inTier.filter((l) => visitedIds.has(l.id));
    return `${done.length}/${inTier.length}`;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading your stamps...</p>
      </div>
    );
  }

  const total = lighthouses.length;
  const stamps = visitedIds.size;
  const pct = total ? Math.round((stamps / total) * 100) : 0;
  const { current: rank, next: nextRank } = rankInfo(stamps);

  return (
    <div className="page dashboard">
      <header className="dash-header">
        <h1>My Stamps</h1>
        <p className="dash-count">
          {stamps} of {total} lighthouses
        </p>
        <p
          className="dash-count"
          style={{ fontSize: '15px', marginTop: '4px', opacity: 0.95 }}
        >
          {rank ? rank.name : 'New Hunter'}
          {nextRank
            ? ` \u00b7 ${nextRank.stamps - stamps} stamp${
                nextRank.stamps - stamps === 1 ? '' : 's'
              } to ${nextRank.name}`
            : stamps >= 183
            ? ' \u00b7 Australia complete'
            : ''}
        </p>
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${pct}%` }}></div>
        </div>
        <div className="tier-summary">
          <div>
            <span className="tier-label">Standard</span>
            <span>{tierCount('Standard')}</span>
          </div>
          <div>
            <span className="tier-label">Extreme</span>
            <span>{tierCount('Extreme')}</span>
          </div>
          <div>
            <span className="tier-label">Heritage</span>
            <span>{tierCount('Heritage')}</span>
          </div>
        </div>
      </header>

      {readOnly && (
        <div
          style={{
            background: '#ffffff',
            border: '2px solid #1b2e4b',
            borderRadius: '10px',
            padding: '14px',
            margin: '0 0 12px',
          }}
        >
          <p style={{ margin: '0 0 8px', fontSize: '16px', color: '#000000', lineHeight: 1.5 }}>
            Your membership has ended, but your passport is yours to keep. Every
            stamp, photo and note stays right here. Renew whenever you are ready to
            keep hunting.
          </p>
          <Link to="/join" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
            Renew membership
          </Link>
        </div>
      )}

      <div style={{ margin: '0 0 12px' }}>
        <Link to="/print-map" className="btn-secondary" style={{ textDecoration: 'none' }}>
          Print my map
        </Link>
      </div>

      <div className="filters">
        <select value={tier} onChange={(e) => setTier(e.target.value)}>
          {TIERS.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
        <select value={state} onChange={(e) => setState(e.target.value)}>
          {states.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </div>

      {!readOnly && (
      <div style={{ margin: '4px 0 12px' }}>
        <button
          className="btn-secondary"
          onClick={() => {
            setShowSubmit(!showSubmit);
            setSubMessage('');
          }}
        >
          {showSubmit ? 'Close past visits' : 'Submit past visits'}
        </button>
        {showSubmit && (
          <div
            style={{
              background: '#ffffff',
              border: '1px solid #d7dce5',
              borderRadius: '10px',
              padding: '14px',
              marginTop: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <p className="stamp-meta" style={{ margin: 0 }}>
              Visited a lighthouse before joining? Pick it, add the date, and attach a
              photo of yourself there. We verify and stamp it in.
            </p>
            <select
              value={subTier}
              onChange={(e) => {
                setSubTier(e.target.value);
                setSubLighthouseId('');
              }}
            >
              <option>Standard</option>
              <option>Extreme</option>
              <option>Heritage</option>
            </select>
            <select
              value={subLighthouseId}
              onChange={(e) => setSubLighthouseId(Number(e.target.value) || '')}
            >
              <option value="">Select a lighthouse</option>
              {lighthouses
                .filter((l) => l.passport_type === subTier)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
            </select>
            <label className="stamp-meta" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              Visit date
              <input type="date" value={subDate} onChange={(e) => setSubDate(e.target.value)} />
            </label>
            <label className="stamp-meta" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              Photo of you at the lighthouse (required, it is how we verify)
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setSubPhoto(e.target.files?.[0] || null)}
              />
            </label>
            <button className="btn-primary" onClick={submitPastVisit} disabled={subBusy}>
              {subBusy ? 'Submitting...' : 'Submit for review'}
            </button>
            {subMessage && <p className="stamp-meta" style={{ margin: 0 }}>{subMessage}</p>}
          </div>
        )}
      </div>

      )}

      <label
        className="stamp-meta"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          margin: '0 0 12px',
          fontSize: '16px',
          color: '#000000',
        }}
      >
        <input type="checkbox" checked={sharePhotos} onChange={toggleSharePhotos} />
        Share my photos with other members
      </label>

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={onPhotoChosen}
      />

      <ul className="stamp-list">
        {filtered.map((l) => {
          const done = visitedIds.has(l.id);
          const pending = pendingIds.has(l.id);
          const visit = visitFor(l.id);
          const open = openId === l.id;
          const busy = uploadingId === l.id;
          return (
            <li
              key={l.id}
              className={`stamp-card ${done ? 'visited' : ''} tappable`}
              onClick={() => setOpenId(open ? null : l.id)}
            >
              <div className="stamp-row">
                {visit && photosFor(visit.id).length > 0 ? (
                  <img
                    className="stamp-thumb"
                    src={photosFor(visit.id)[0].url}
                    alt=""
                  />
                ) : visit ? (
                  <div className="stamp-thumb stamp-thumb-empty">
                    <Camera size={18} />
                  </div>
                ) : null}
                <div className="stamp-text">
                  <p className="stamp-name">{l.name}</p>
                  <p className="stamp-meta">
                    {l.passport_type} · {l.state}
                  </p>
                </div>
                {done && <span className="badge badge-done">Stamped</span>}
                {pending && !done && (
                  <span className="badge badge-pending">Pending</span>
                )}
              </div>
              {open && (
                <div className="stamp-detail" onClick={(e) => e.stopPropagation()}>
                  <dl className="lh-facts">
                    {l.year_established && (
                      <div><dt>Established</dt><dd>{l.year_established}</dd></div>
                    )}
                    {l.status && <div><dt>Status</dt><dd>{l.status}</dd></div>}
                    {l.access && <div><dt>Access</dt><dd>{l.access}</dd></div>}
                    {l.amsa && <div><dt>AMSA</dt><dd>{l.amsa}</dd></div>}
                  </dl>
                  {l.notes && <p className="lh-notes">{l.notes}</p>}
                  <a
                    className="lh-map-link"
                    href={l.maps_url || `https://www.google.com/maps?q=${l.latitude},${l.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLink size={14} /> Open in Google Maps
                  </a>
                  <button
                    className="btn-secondary"
                    style={{ marginTop: '8px' }}
                    onClick={() => setDetailLh(l)}
                  >
                    Full details and member photos
                  </button>
                  {visit && (
                    <p className="stamp-meta">
                      Visited {new Date(visit.visited_at).toLocaleDateString('en-AU')}
                    </p>
                  )}
                  {visit && photosFor(visit.id).length > 0 && (
                    <div className="photo-grid">
                      {photosFor(visit.id).map((p) => (
                        <div key={p.id} className="photo-cell">
                          <img
                            src={p.url}
                            alt={l.name}
                            onClick={() => setViewing(p)}
                          />
                          <button
                            className="photo-remove"
                            aria-label="Remove photo"
                            onClick={() => removePhoto(p)}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {!readOnly && visit && photosFor(visit.id).length < MAX_PHOTOS_PER_STAMP && (
                    <button
                      className="btn-secondary"
                      onClick={() => pickPhoto(visit)}
                      disabled={busy}
                    >
                      <Camera size={16} />
                      {busy ? 'Uploading...' : 'Add photos'}
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {viewing && (
        <div className="lightbox" onClick={() => setViewing(null)}>
          <img src={viewing.url} alt="" />
        </div>
      )}
      {detailLh && (
        <LighthouseDetail lighthouse={detailLh} onClose={() => setDetailLh(null)} />
      )}
    </div>
  );
}

export default DashboardPage;
